// ── bulk-import-collection: preview + result tables (modal, urlSync:false) ───

import { useMemo } from "react";
import { Badge, humanizeLabel, useCollection, type CollectionColumnDef, type UseCollectionResult } from "@vjal/ui";

import type { ParsedImportRow } from "./bulk-import-parse";
import type { BulkResultRow } from "./bulk-import-result";

export type PreviewTableRow = ParsedImportRow & Record<string, unknown>;
export type ResultTableRow = BulkResultRow & Record<string, unknown>;

function previewTone(status: ParsedImportRow["status"]): "success" | "warning" | "danger" {
  if (status === "ready") return "success";
  if (status === "duplicate") return "warning";
  return "danger";
}

export function buildBulkImportPreviewCollectionColumns(): CollectionColumnDef<PreviewTableRow>[] {
  return [
    { id: "row", header: "Row", priority: 1, cell: (row) => String(row.row) },
    { id: "email", header: "Email", priority: 1, searchValue: (row) => row.email ?? "", cell: (row) => row.email || "—" },
    {
      id: "name",
      header: "Name",
      priority: 2,
      searchValue: (row) => row.name || [row.firstName, row.lastName].filter(Boolean).join(" "),
      cell: (row) => row.name || [row.firstName, row.lastName].filter(Boolean).join(" ") || "—"
    },
    {
      id: "status",
      header: "Result",
      priority: 2,
      sortable: true,
      sortAccessor: (row) => row.status,
      cell: (row) => <Badge variant={previewTone(row.status)}>{humanizeLabel(row.status)}</Badge>
    },
    { id: "error", header: "Detail", priority: 3, cell: (row) => row.error ?? "Ready to import" }
  ];
}

export function buildBulkImportResultCollectionColumns(): CollectionColumnDef<ResultTableRow>[] {
  return [
    { id: "row", header: "Row", priority: 1, cell: (row) => String(row.row) },
    { id: "email", header: "Email", priority: 1, searchValue: (row) => row.email ?? "", cell: (row) => row.email || "—" },
    {
      id: "status",
      header: "Outcome",
      priority: 2,
      sortable: true,
      sortAccessor: (row) => row.status,
      cell: (row) => <Badge variant={row.ok ? "success" : "danger"}>{humanizeLabel(row.status)}</Badge>
    }
  ];
}

export function useBulkImportPreviewCollection(rows: PreviewTableRow[]): UseCollectionResult<PreviewTableRow> {
  const columns = useMemo(() => buildBulkImportPreviewCollectionColumns(), []);

  return useCollection<PreviewTableRow>({
    mode: "client",
    rows,
    columns,
    pageSize: 25,
    urlSync: false,
    getRowKey: (row) => `row-${row.row}`
  });
}

export function useBulkImportResultCollection(rows: ResultTableRow[]): UseCollectionResult<ResultTableRow> {
  const columns = useMemo(() => buildBulkImportResultCollectionColumns(), []);

  return useCollection<ResultTableRow>({
    mode: "client",
    rows,
    columns,
    pageSize: 25,
    urlSync: false,
    getRowKey: (row) => `result-${row.row}`
  });
}